import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table.tsx";
import { Content } from "@/components/layouts/content";
import { allUserSessionsQueryOptions } from "@/features/settings/api/queries/all-user-sessions-query";
import type { AllUserSession } from "@/features/settings/api/services/all-user-sessions-service";
import { formatDate } from "@/utils/format-date";
import { IconDeviceDesktop, IconDeviceMobile, IconRefresh } from "@tabler/icons-react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";
import { z } from "zod";

export const Route = createFileRoute("/settings/active-user-sessions")({
	loader: ({ context: { queryClient } }) => {
		return queryClient.ensureQueryData(allUserSessionsQueryOptions({ status: "active" }));
	},
	component: ActiveUserSessionsPage,
});

function ActiveUserSessionsPage() {
	const queryClient = useQueryClient();

	const { data: sessionsData } = useSuspenseQuery(allUserSessionsQueryOptions({ status: "active" }));
	const allSessions = sessionsData?.data || [];

	// Filter out expired sessions (older than 24 hours of inactivity)
	const sessions = useMemo(() => {
		const now = new Date();
		const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

		return allSessions.filter((session) => {
			// If session is logged out, don't show it
			if (session.is_logged_out) return false;

			// If session has recent activity (within 24 hours), show it
			if (session.last_used_at) {
				const lastUsed = new Date(session.last_used_at);
				return lastUsed > twentyFourHoursAgo;
			}

			// If no last_used_at but created recently (within 24 hours), show it
			const created = new Date(session.created_at);
			return created > twentyFourHoursAgo;
		});
	}, [allSessions]);

	const handleRefresh = () => {
		queryClient.invalidateQueries({ queryKey: ["all-user-sessions"] });
	};

	const getDeviceIcon = (userAgent?: string) => {
		if (!userAgent) return <IconDeviceDesktop size={16} />;
		const isMobile = /Mobile|Android|iPhone|iPad/i.test(userAgent);
		return isMobile ? <IconDeviceMobile size={16} /> : <IconDeviceDesktop size={16} />;
	};

	const getBrowserInfo = (userAgent?: string): string => {
		if (!userAgent) return "Unknown";

		let browser = "Unknown";
		if (userAgent.includes("Chrome")) browser = "Chrome";
		else if (userAgent.includes("Firefox")) browser = "Firefox";
		else if (userAgent.includes("Safari")) browser = "Safari";
		else if (userAgent.includes("Edge")) browser = "Edge";

		let device = "Desktop";
		if (/Mobile|Android/i.test(userAgent)) device = "Mobile";
		else if (/iPad|Tablet/i.test(userAgent)) device = "Tablet";

		return `${browser} on ${device}`;
	};

	const columns: ColumnDef<AllUserSession>[] = [
		{
			id: "user",
			label: "User",
			cell: ({ data }) => (
				<div className="flex flex-col">
					<span className="font-semibold text-foreground-dark">{data.user_name}</span>
					<span className="text-sm text-foreground/70">{data.user_email}</span>
					{data.user_role && <span className="text-xs text-foreground/50">{data.user_role}</span>}
				</div>
			),
		},
		{
			id: "session",
			label: "Session Details",
			cell: ({ data }) => (
				<div className="flex items-center gap-2">
					<div className="text-foreground/60">{getDeviceIcon(data.user_agent)}</div>
					<div className="flex flex-col">
						<span className="text-sm font-medium">{getBrowserInfo(data.user_agent)}</span>
						{data.ip_address && <span className="text-xs text-foreground/60">IP: {data.ip_address}</span>}
					</div>
				</div>
			),
		},
		{
			id: "last_activity",
			label: "Last Activity",
			cell: ({ data }) => (
				<div className="text-sm text-foreground/70">
					{data.last_used_at ? (
						<span>Active {formatDistanceToNow(new Date(data.last_used_at), { addSuffix: true })}</span>
					) : (
						<span>Started {formatDistanceToNow(new Date(data.created_at), { addSuffix: true })}</span>
					)}
				</div>
			),
		},
		{
			id: "created",
			label: "Session Started",
			cell: ({ data }) => <div className="text-sm text-foreground/70">{formatDate(data.created_at, "PP p")}</div>,
		},
	];

	return (
		<Content pageName="Active User Sessions">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				{/* Header */}
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Active User Sessions</span>
						<span className="text-base text-foreground/70">Monitor active user login sessions across the system</span>
					</div>
					<div className="flex flex-row gap-2">
						<Button variant="ghost" size="sm" onClick={handleRefresh}>
							<IconRefresh size={16} />
							Refresh
						</Button>
					</div>
				</div>

				{/* Sessions Table */}
				<div className="flex flex-col w-full gap-4">
					<DataTable showToolbar={true} data={sessions} columns={columns} showBorder={true} compact={true} />
				</div>

				{sessions.length === 0 && (
					<div className="p-8 text-center text-foreground/70 border border-border/50 rounded-lg">
						<IconDeviceDesktop size={48} className="mx-auto mb-2 opacity-50" />
						<p>No active sessions found</p>
					</div>
				)}
			</div>
		</Content>
	);
}
